import { useIntl } from 'react-intl'
import DataTable, {
	TableBody,
	TableColumn,
	TableHeader,
	TableRow,
} from 'react-md/lib/DataTables'
import { TableContainer } from 'ui/StyledComponents/Tables'
import type { LoanFilters } from './types'
import { loanTableHeaders } from './loanHeaders'
import LoansTableRow from './LoansTableRow'
import Pagination from 'ui/components/Pagination'
import FontIcon from 'react-md/lib/FontIcons'
import { TableHeaderNameSortable } from './components'
import { ListItem } from 'react-md/lib/Lists'
import { useState } from 'react'
import { _deleteLoan } from 'state/finance-loan.actions'
import useDialogs from 'ui/components/dialogs/useDialogs'
import { GENERIC_ERROR_NOTIFICATION } from 'ui/messages'
import type { Loan } from '@viilu-web/shared/types/loans'

type LoansTableProps = {
	loans: Loan[]
	totalItemCount: number
	itemsPerPage: number
	filters: LoanFilters
	setFilters: React.Dispatch<React.SetStateAction<LoanFilters>>
	onDraftLoanClick: (loan: Loan) => void
	refreshLoans: () => void
}

function LoansTable(props: LoansTableProps) {
	const {
		loans,
		totalItemCount,
		itemsPerPage,
		filters,
		setFilters,
		onDraftLoanClick,
		refreshLoans,
	} = props
	const intl = useIntl()
	const { confirm, alert } = useDialogs()
	const [deleting, setDeleting] = useState<string[]>([])

	const handlePaginationBack = () => {
		const paginationPage = filters.paginationPage
		if (paginationPage === 0) {
			return
		}
		const pageIndex = paginationPage - 1
		setFilters((prevData) => ({
			...prevData,
			paginationPage: pageIndex,
		}))
	}
	const handlePaginationNext = () => {
		const lastPageIndex = Math.ceil(totalItemCount / itemsPerPage)
		const paginationPage = filters.paginationPage
		if (paginationPage === lastPageIndex) {
			return
		}
		const pageIndex = paginationPage + 1
		setFilters((prevData) => ({
			...prevData,
			paginationPage: pageIndex,
		}))
	}

	const deleteLoan = async (uuid: string | undefined) => {
		if (!uuid || uuid.length === 0) {
			return
		}

		const confirmMessage = intl.formatMessage({
			defaultMessage: 'Haluatko varmasti poistaa lainan?',
			description: 'Confirm message before deleting a loan.',
		})

		if (!(await confirm(confirmMessage))) {
			return
		}

		setDeleting((prev) => [...prev, uuid])
		try {
			const result = await _deleteLoan(uuid)
			if (result?.ok) {
				refreshLoans()
			} else {
				throw new Error('Delete loan failed.')
			}
		} catch (error) {
			console.error(error)
			alert(GENERIC_ERROR_NOTIFICATION(intl))
		}
		setDeleting((prev) => prev.filter((u) => u !== uuid))
	}

	const changeSort = (key: keyof Loan) => {
		setFilters((prevData) => {
			const existingSort = prevData.sortBy

			return {
				...prevData,
				sortBy:
					existingSort?.column === key
						? {
								column: key,
								order: existingSort.order === 'asc' ? 'desc' : 'asc',
						  }
						: { column: key, order: 'asc' },
			}
		})
	}

	const renderTableHeaders = () => {
		return (
			<TableRow>
				{loanTableHeaders.map((header) => {
					return (
						<TableColumn
							key={header.key}
							onClick={() =>
								header.sortable && changeSort(header.key as keyof Loan)
							}
							style={{ cursor: header.sortable ? 'pointer' : '' }}
						>
							<TableHeaderNameSortable>
								<p className="text-strong text-subtle">{header.title(intl)}</p>
								{filters.sortBy.column === header.key && (
									<FontIcon style={{ fontSize: 12 }}>
										{filters.sortBy.order === 'asc'
											? 'arrow_upward'
											: 'arrow_downward'}
									</FontIcon>
								)}
							</TableHeaderNameSortable>
						</TableColumn>
					)
				})}
				<TableColumn key={'children-header'}></TableColumn>
			</TableRow>
		)
	}

	return (
		<TableContainer>
			{loans ? (
				<TableContainer>
					<DataTable
						plain
						style={{
							background: 'white',
							maxHeight: 'calc(100vh - 205px)',
						}}
					>
						<TableHeader
							style={{
								position: 'sticky',
								top: 0,
								zIndex: 1,
								background: 'white',
							}}
						>
							{renderTableHeaders()}
						</TableHeader>
						<TableBody>
							{loans
								.map((loan) => (
									<LoansTableRow
										key={loan.uuid}
										loan={loan}
										onDraftLoanClick={onDraftLoanClick}
										processing={deleting.includes(loan.uuid || '')}
										menuItems={[
											<ListItem
												key="delete"
												primaryText={intl.formatMessage({
													defaultMessage: 'Poista',
													description: 'Delete loan',
												})}
												leftIcon={<FontIcon>delete</FontIcon>}
												onClick={() => deleteLoan(loan.uuid)}
												className="md-divider-border md-divider-border--right"
											/>,
										]}
									/>
								))
								.concat(
									<tr
										key="empty-row"
										className="full-width"
										style={{ height: 100, background: 'white' }}
									></tr>,
								)}
						</TableBody>
						<Pagination
							rowCount={totalItemCount}
							pageIndex={filters.paginationPage}
							rowsPerPage={itemsPerPage}
							onBack={handlePaginationBack}
							onNext={handlePaginationNext}
						/>
					</DataTable>
				</TableContainer>
			) : null}
		</TableContainer>
	)
}

export default LoansTable
